import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const WHATSAPP_NUMBER = "5561982574468";
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Olá, vim pelo site e gostaria de mais informações",
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

const colors = {
  navy: "#0A1628",
  navyLight: "#0F2040",
  blue: "#1565C0",
  blueLight: "#1E88E5",
  blueBright: "#42A5F5",
  accent: "#00B4D8",
  accentLight: "#48CAE4",
  white: "#FFFFFF",
  offWhite: "#F0F4FA",
  lightGray: "#E8EEF8",
  midGray: "#8FA3C8",
  dark: "#06101E",
  cardBg: "#0D1E35",
  glass: "rgba(255,255,255,0.05)",
  glassHover: "rgba(255,255,255,0.1)",
};

/* ==========================================================
   Ajuste as rotas abaixo se os caminhos no seu react-router
   forem diferentes (ex: no App.jsx, <Route path="..." />)
   ========================================================== */
const servicosSubmenu = [
  { label: "Enceradeira", path: "/PaginaEnceradeira" },
  { label: "Lavadora de Alta Pressão", path: "/PaginaLavadora" },
  { label: "Extratora", path: "/PaginaExtratora" },
  { label: "Compressor", path: "/PaginaCompressor" },
];

const IconWhatsApp = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const IconMenu = ({ size = 24, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const IconX = ({ size = 24, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconChevron = ({ size = 14, color = "currentColor", style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicosOpen, setServicosOpen] = useState(false); // dropdown desktop
  const [mobileServicosOpen, setMobileServicosOpen] = useState(false); // accordion mobile
  const closeTimer = useRef(null);
  const navigate = useNavigate();

  const handleHome = () => {
    setMenuOpen(false);
    navigate("/");
  };

  const handleNavigate = (path) => {
    setMenuOpen(false);
    setServicosOpen(false);
    setMobileServicosOpen(false);
    navigate(path);
  };

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Pequeno delay ao sair com o mouse, para não fechar o menu sem querer
  // ao passar entre o botão "Serviços" e a lista de opções
  const openServicos = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setServicosOpen(true);
  };
  const scheduleCloseServicos = () => {
    closeTimer.current = setTimeout(() => setServicosOpen(false), 150);
  };

  const navLinks = [
    { label: "Galeria", href: "#galeria" },
    { label: "Contato", href: "#contato" },
  ];

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: "0 24px",
        background: scrolled ? "rgba(6,16,30,0.95)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? `1px solid rgba(21,101,192,0.2)` : "none",
        transition: "all 0.4s ease",
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 72,
        }}
      >
        {/* Logo */}
        <div
          onClick={handleHome}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 35,
              height: 35,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${colors.blue}, ${colors.accent})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src="/logo-soluteck.jpg"
              alt="Soluteck"
              style={{ height: 45, objectFit: "contain" }}
            />
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 800,
                fontSize: 18,
                lineHeight: 1,
                color: colors.white,
              }}
            >
              Soluteck
            </div>
            <div
              style={{
                fontSize: 10,
                color: colors.midGray,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
              }}
            >
              Assistência
            </div>
          </div>
        </div>

        {/* Desktop nav */}
        <nav
          className="nav-desktop"
          style={{ display: "flex", gap: 36, alignItems: "center" }}
        >
          <button
            onClick={handleHome}
            className="nav-link"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: colors.midGray,
              fontSize: 15,
              fontWeight: 500,
              fontFamily: "inherit",
              padding: 0,
            }}
          >
            Início
          </button>

          {/* Serviços com dropdown ao passar o mouse */}
          <div
            style={{ position: "relative" }}
            onMouseEnter={openServicos}
            onMouseLeave={scheduleCloseServicos}
          >
            <button
              className="nav-link"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: colors.midGray,
                fontSize: 15,
                fontWeight: 500,
                fontFamily: "inherit",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              Serviços
              <IconChevron
                size={13}
                style={{
                  transition: "transform 0.25s ease",
                  transform: servicosOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>

            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: servicosOpen
                  ? "translateX(-50%) translateY(6px)"
                  : "translateX(-50%) translateY(-4px)",
                minWidth: 220,
                paddingTop: 14,
                opacity: servicosOpen ? 1 : 0,
                visibility: servicosOpen ? "visible" : "hidden",
                pointerEvents: servicosOpen ? "auto" : "none",
                transition: "opacity 0.2s ease, transform 0.2s ease",
              }}
            >
              <div
                style={{
                  background: colors.cardBg,
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 14,
                  padding: 8,
                  boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
                  backdropFilter: "blur(20px)",
                }}
              >
                {servicosSubmenu.map(({ label, path }) => (
                  <button
                    key={path}
                    onClick={() => handleNavigate(path)}
                    className="servicos-dropdown-item"
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: colors.lightGray,
                      fontSize: 14.5,
                      fontWeight: 500,
                      fontFamily: "inherit",
                      padding: "10px 14px",
                      borderRadius: 8,
                      transition: "background 0.2s ease, color 0.2s ease",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {navLinks.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              className="nav-link"
              style={{
                color: colors.midGray,
                textDecoration: "none",
                fontSize: 15,
                fontWeight: 500,
              }}
            >
              {label}
            </a>
          ))}

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{
              background: `linear-gradient(135deg, ${colors.blue}, ${colors.accent})`,
              color: colors.white,
              textDecoration: "none",
              padding: "10px 22px",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <IconWhatsApp size={16} /> Fale Conosco
          </a>
        </nav>

        {/* Mobile menu button */}
        <button
          className="mobile-menu-btn"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: colors.white,
            padding: 8,
          }}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? (
            <IconX size={24} color={colors.white} />
          ) : (
            <IconMenu size={24} color={colors.white} />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`mobile-menu ${menuOpen ? "mobile-menu-open" : ""}`}
        style={{
          flexDirection: "column",
          background: "rgba(6,16,30,0.98)",
          backdropFilter: "blur(20px)",
          padding: "16px 0 24px",
          borderTop: `1px solid rgba(21,101,192,0.2)`,
        }}
      >
        <button
          onClick={handleHome}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            color: colors.midGray,
            padding: "14px 24px",
            fontSize: 16,
            fontWeight: 500,
            fontFamily: "inherit",
            borderBottom: `1px solid rgba(255,255,255,0.05)`,
          }}
        >
          Início
        </button>

        {/* Serviços em accordion no mobile (toque para abrir) */}
        <div style={{ borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
          <button
            onClick={() => setMobileServicosOpen(!mobileServicosOpen)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              background: "none",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              color: colors.midGray,
              padding: "14px 24px",
              fontSize: 16,
              fontWeight: 500,
              fontFamily: "inherit",
            }}
          >
            Serviços
            <IconChevron
              size={16}
              style={{
                transition: "transform 0.25s ease",
                transform: mobileServicosOpen
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
              }}
            />
          </button>
          <div
            style={{
              display: mobileServicosOpen ? "flex" : "none",
              flexDirection: "column",
              background: "rgba(255,255,255,0.03)",
            }}
          >
            {servicosSubmenu.map(({ label, path }) => (
              <button
                key={path}
                onClick={() => handleNavigate(path)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  color: colors.lightGray,
                  padding: "12px 24px 12px 40px",
                  fontSize: 15,
                  fontWeight: 500,
                  fontFamily: "inherit",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {navLinks.map(({ label, href }) => (
          <a
            key={href}
            href={href}
            onClick={() => setMenuOpen(false)}
            style={{
              color: colors.midGray,
              textDecoration: "none",
              padding: "14px 24px",
              fontSize: 16,
              fontWeight: 500,
              borderBottom: `1px solid rgba(255,255,255,0.05)`,
            }}
          >
            {label}
          </a>
        ))}

        <div style={{ padding: "16px 24px 0" }}>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: `linear-gradient(135deg, ${colors.blue}, ${colors.accent})`,
              color: colors.white,
              textDecoration: "none",
              padding: "14px 22px",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <IconWhatsApp size={18} /> Fale pelo WhatsApp
          </a>
        </div>
      </div>

      <style>{`
        .servicos-dropdown-item:hover {
          background: ${colors.glassHover};
          color: ${colors.white};
        }

        /* ------- Responsivo ------- */

        /* Botão hambúrguer: escondido no desktop por padrão */
        .mobile-menu-btn {
          display: none;
        }

        /* Menu mobile: escondido por padrão, some do fluxo do layout */
        .mobile-menu {
          display: none;
        }
        .mobile-menu.mobile-menu-open {
          display: flex;
        }

        @media (max-width: 860px) {
          /* Esconde a navegação de desktop (links + botão WhatsApp) */
          .nav-desktop {
            display: none !important;
          }
          /* Mostra o botão hambúrguer */
          .mobile-menu-btn {
            display: flex !important;
            align-items: center;
            justify-content: center;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;
